import { useEffect, useState } from "react";
import FlexRow from "../../../../../components/base/FlexRow";
import Icon from "../../../../../components/base/Icon";
import { Drawer } from "../../../../../components/drawers/Drawer";
import { DataTable, DataTableField } from "../../../../../components/data/DataTable";
import { JobsChartSegments } from "../../../../../core/Enums";
import { closeReportJobsList, toPrettyDate } from "../../../../../core/Global";
import Job from "../../models/Job";
import JobReportListModel from "../../../../reports/jobs/models/JobReportListModel";
import { FilterParam } from "../../../../../models/BrowseOptions";
import { JobsReportRequest, effectiveDateType } from "../../../../reports/jobs/models/JobsReportRequest";

interface IProps {
    list: JobReportListModel
}
export default function JobsListModal(props: IProps) {

    const JobIdColumn = (data: Job) => (
        <div>
            <a href={"/crm/jobs/overview/" + data.id} target="_blank">
                #{data.jobId}
            </a>
        </div>
    )

    const ClientColumn = (data: Job) => (
        <div>
            <div>
                {data.fullName}
            </div>
            <FlexRow>
                <Icon icon="phone-rotary" className="text-muted me-1" />
                <span className="fs-8 text-muted">
                    {data.phone?.phoneFriendly}
                </span>
            </FlexRow>
        </div>
    )

    const AddressColumn = (data: Job) => (
        <div>
            {data.address?.title}
        </div>
    )

    const StatusColumn = (data: Job) => (
        <div>
            <FlexRow className="mb-1">
                <Icon icon="circle" type="solid" color={data.jobStatus?.color} />
                <span className="ms-1">{data.jobStatus?.name}</span>
            </FlexRow>
            {
                data.isCanceled && data.lastStatusNote &&
                <div className="ms-1 text-gray-700 fs-8">{data.lastStatusNote}</div>
            }
        </div>
    )

    const TechColumn = (data: Job) => (
        <div>
            {data.tech?.fullName}
        </div>
    )

    const AppointmentColumn = (data: Job) => (
        <div className="ms-3">
            <div className="fs-7 fw-bold">
                {data.appointmentDate}
            </div>
            <div className="fs-8">
                {data.appointmentTimeFrom} - {data.appointmentTimeTo}
            </div>
        </div>
    )

    // Same timezone as Last Update: the job's own, which is what the report counts in by default.
    const CreatedColumn = (data: Job) => (
        <div>
            <div className='d-flex align-items-center'>
                {toPrettyDate(data.createdAt, data.timezoneOffset)}
            </div>
            <FlexRow className='fs-8 mt-1'>
                <div>
                    <Icon icon="clock" className="me-2" size={10} type="solid" />
                </div>
                <div className="fs-8 limited-text w-75px">
                    {data.timezone ?? "UTC"}
                </div>
            </FlexRow>
            {
                data.creatorUser &&
                <FlexRow className='fs-8 mt-1'>
                    <div>
                        <Icon icon="user" className="me-2" size={10} type="solid" />
                    </div>
                    <div className="fs-8">
                        By: {data.creatorUser.fullName}
                    </div>
                </FlexRow>
            }

        </div>
    )

    const LastUpdateColumn = (data: Job) => (
        <div>
            <div className='d-flex align-items-center'>
                {toPrettyDate(data.lastStatusUpdatedAt, data.timezoneOffset)}
            </div>
            <FlexRow className='fs-8 mt-1'>
                <div>
                    <Icon icon="clock" className="me-2" size={10} type="solid" />
                </div>
                <div className="fs-8 limited-text w-75px">
                    {data.timezone ?? "UTC"}
                </div>
            </FlexRow>

        </div>
    )

    const subtitle = () => {
        var res = "";
        if (props.list.segment == JobsChartSegments.Dispatchers) res = "Dispatcher";
        else if (props.list.segment == JobsChartSegments.Technician) res = "Technician";
        else if (props.list.segment == JobsChartSegments.Metro) res = "Metro";
        else if (props.list.segment == JobsChartSegments.JobSource) res = "Job Source";
        else if (props.list.segment == JobsChartSegments.JobType) res = "Job Type";

        res += " | " + props.list.name + " | ";

        if (props.list.status) res += props.list.status.name;
        else res += "All Jobs";

        return res;
    }

    // The list behind a report number is asked of the report itself (reports/jobs/drill), with
    // the report's own settings: its date range, date basis and timezone, its filter bar's
    // filters, the row and the status column. Rebuilt on the general jobs list it ignored the
    // date basis and the filters, and its count drifted from the number that was clicked.
    const [filter, filterSet] = useState<FilterParam[]>()
    useEffect(() => {
        var fl: FilterParam[] = [];
        const add = (name: string, value?: string | number) => {
            if (value !== undefined && value !== null && value !== "") fl.push({ name, value: String(value) });
        }

        add("Segment", props.list.segment);
        add("SegmentId", props.list.segmentId);
        add("StatusId", props.list.status?.id);
        add("DateId", props.list.dateId);
        add("StartDate", props.list.startDate);
        add("ToDate", props.list.toDate);
        add("Timezone", props.list.timezone);
        add("DateType", props.list.dateType);

        // The filter bar's filters, exactly as the report sent them.
        new URLSearchParams(props.list.filterQuery ?? "").forEach((value, name) => add(name, value));

        filterSet(f => [...fl])

    }, [props.list])

    // The date column follows the report's date basis, resolved the way the report resolves it
    // (an unset basis is the segment's default), so the list shows the date that was counted.
    const byCreatedDate = effectiveDateType({ segment: props.list.segment, dateType: props.list.dateType } as JobsReportRequest) == "2"

    return (
        <Drawer id="drawer-viewjob-list"
            title="Jobs List"
            subtitle={subtitle()}
            size="xlarge"
            onClose={closeReportJobsList}
        >
            <div>
                {
                    filter &&
                    <DataTable isChild
                        objectName="Job"
                        preventCreate noHeader noAction noFilter
                        apiRoute="/reports/jobs/drill/"
                        defaultFilters={filter}
                        ignoreUrlParams
                        defaultSize={10}
                    >

                        <DataTableField title="Job ID" width={70}>
                            <JobIdColumn />
                        </DataTableField>
                        <DataTableField title="Client" width={150}>
                            <ClientColumn />
                        </DataTableField>
                        <DataTableField title="Address" maxWidth={200}>
                            <AddressColumn />
                        </DataTableField>

                        <DataTableField title="Status">
                            <StatusColumn />
                        </DataTableField>

                        <DataTableField title="Job Type" dataValueName="jobType" maxWidth={150} />
                        <DataTableField title="Job Source" dataValueName="jobSource" maxWidth={150} />

                        <DataTableField title="Tech" maxWidth={150}>
                            <TechColumn />
                        </DataTableField>

                        <DataTableField title="Appointment" maxWidth={150}>
                            <AppointmentColumn />
                        </DataTableField>

                        {
                            byCreatedDate
                                ? <DataTableField title="Created At" maxWidth={150}>
                                    <CreatedColumn />
                                </DataTableField>
                                : <DataTableField title="Last Update" maxWidth={150}>
                                    <LastUpdateColumn />
                                </DataTableField>
                        }
                    </DataTable>
                }

            </div>
        </Drawer>
    )
}